import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Button, Pagination, Alert, Modal, Spinner } from 'react-bootstrap';
import ActionTooltipButton from './ActionTooltipButton';
import Swal from '../helpers/swal';
import { toast } from 'react-toastify';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import CampaignTemplateFilters from './CampaignTemplateFilters';
import { truncateText } from '../process/processQueueUtils';
function CampaignTemplateTable({ searchPanelOpen = false }) {
    const navigate = useNavigate();
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    const fetchList = useCallback(() => {
        const queryOptions = {
            sortBy,
            sortOrder,
            searchTerm: appliedSearch,
            typeFilter,
            activeFilter,
        };
        setIsLoading(true);
        setError(null);
        campaignService
            .getAllTemplates(currentPage, itemsPerPage, queryOptions)
            .then((res) => {
                setTemplates(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if (
                    (res.data.content || []).length === 0 &&
                    (appliedSearch || typeFilter || activeFilter)
                ) {
                    setError('No templates match your filters.');
                }
            })
            .catch(() => {
                setError('Failed to load templates. Please try again.');
                setTemplates([]);
            })
            .finally(() => setIsLoading(false));
    }, [currentPage, itemsPerPage, sortBy, sortOrder, appliedSearch, typeFilter, activeFilter]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setTypeFilter('');
        setActiveFilter('');
        setSortBy('createdAt');
        setSortOrder('DESC');
        setCurrentPage(0);
    };

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setCurrentPage(0);
    };

    const handleActiveFilterChange = (value) => {
        setActiveFilter(value);
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleEdit = (id) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            const route = template.type === 'EMAIL' ? 'emailtemplate' : 'smstemplate';
            navigate(`${route}/${id}`);
        }
    };

    const handleClosePreview = () => {
        setPreviewTemplate(null);
        setPreviewContent(null);
        setPreviewError(null);
        setPreviewLoading(false);
    };

    const handlePreview = (item) => {
        setPreviewTemplate(item);
        setPreviewContent(null);
        setPreviewError(null);
        setPreviewLoading(true);

        campaignService
            .getTemplateById(item.id)
            .then((res) => {
                setPreviewContent(res.data);
            })
            .catch(() => {
                setPreviewError('Failed to load template preview. Content may be missing or unavailable.');
            })
            .finally(() => setPreviewLoading(false));
    };

    const handleAddNewEmail = () => {
        navigate('emailtemplate/new');
    };

    const handleAddNewSms = () => {
        navigate('smstemplate/new');
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: 'Delete template?',
            text: `"${item.name || 'Untitled'}" will be permanently deleted.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (result.isConfirmed) {
                campaignService
                    .deleteTemplate(item.id)
                    .then(() => {
                        toast.success('Template deleted', { position: toast.POSITION.BOTTOM_RIGHT });
                        fetchList();
                    })
                    .catch(() => {
                        toast.error('Failed to delete template', { position: toast.POSITION.BOTTOM_RIGHT });
                    });
            }
        });
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = templates.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const TypeBadge = ({ type }) =>
        type === 'EMAIL' ? (
            <span className="badge bg-primary bg-opacity-10 text-primary">
                <i className="bi bi-envelope me-1"></i>
                Email
            </span>
        ) : (
            <span className="badge bg-success bg-opacity-10 text-success">
                <i className="bi bi-chat-dots me-1"></i>
                SMS
            </span>
        );

    const ActiveBadge = ({ isActive }) =>
        isActive ? (
            <span className="badge text-bg-success">
                <i className="bi bi-check-circle me-1"></i>
                Active
            </span>
        ) : (
            <span className="badge text-bg-secondary">
                <i className="bi bi-dash-circle me-1"></i>
                Inactive
            </span>
        );

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    return (
        <>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center flex-wrap gap-1">
                    <i className="bi bi-card-heading text-primary me-1"></i>
                    <h5 className="card-title mb-0" style={{ color: '#012970' }}>
                        Template List
                        <span className="text-muted fw-normal" style={{ fontSize: '0.9rem', marginLeft: '0.35rem' }}>
                            ({totalElements} template{totalElements !== 1 ? 's' : ''} total)
                        </span>
                    </h5>
                </div>
                <div>
                    <Button variant="outline-primary me-2" size="sm" onClick={() =>handleAddNewEmail()}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Email Template
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => handleAddNewSms()}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Add SMS Template
                    </Button>
                </div>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <CampaignTemplateFilters
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        onSearchSubmit={handleSearchSubmit}
                        typeFilter={typeFilter}
                        onTypeFilterChange={handleTypeFilterChange}
                        activeFilter={activeFilter}
                        onActiveFilterChange={handleActiveFilterChange}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || typeFilter || activeFilter || sortBy !== 'createdAt' || sortOrder !== 'DESC')}
                    />
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && templates.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th style={{ width: '160px' }}>Actions</th>
                                <th style={{ width: '56px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-4">
                                        No templates found.
                                    </td>
                                </tr>
                            ) : (
                                templates.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr className={expandedRowId === item.id ? 'table-active' : ''}>
                                            <td>
                                                <div className="fw-medium">{item.name || '—'}</div>
                                            </td>
                                            <td>
                                                <TypeBadge type={item.type} />
                                            </td>
                                            <td>
                                                <ActiveBadge isActive={item.isActive} />
                                            </td>
                                            <td>
                                                <small>{item.createdAt || '—'}</small>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    <ActionTooltipButton
                                                        variant="outline-info"
                                                        size="sm"
                                                        tooltip="Preview"
                                                        tooltipId={`template-preview-${item.id}`}
                                                        onClick={() => handlePreview(item)}
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </ActionTooltipButton>
                                                    <ActionTooltipButton
                                                        variant="outline-primary"
                                                        size="sm"
                                                        tooltip="Edit"
                                                        tooltipId={`template-edit-${item.id}`}
                                                        onClick={() => handleEdit(item.id)}
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </ActionTooltipButton>
                                                    <ActionTooltipButton
                                                        variant="outline-danger"
                                                        size="sm"
                                                        tooltip="Delete"
                                                        tooltipId={`template-delete-${item.id}`}
                                                        onClick={() => handleDelete(item)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </ActionTooltipButton>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant={
                                                        expandedRowId === item.id
                                                            ? 'outline-secondary'
                                                            : 'outline-primary'
                                                    }
                                                    size="sm"
                                                    onClick={() => toggleRow(item.id)}
                                                    title={expandedRowId === item.id ? 'Collapse' : 'Expand'}
                                                    aria-expanded={expandedRowId === item.id}
                                                >
                                                    <i
                                                        className={`bi bi-chevron-${
                                                            expandedRowId === item.id ? 'up' : 'down'
                                                        }`}
                                                    ></i>
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr className="p-0 border-top-0">
                                            <td colSpan={6} className="p-0 border-top-0">
                                                <Collapse in={expandedRowId === item.id}>
                                                    <div className="p-3 bg-light border-top">
                                                        <div className="row">
                                                            <DetailField label="Template ID" mono>
                                                                {item.id}
                                                            </DetailField>
                                                            <DetailField label="Type">{item.type}</DetailField>
                                                            <DetailField label="Status">
                                                                <ActiveBadge isActive={item.isActive} />
                                                            </DetailField>
                                                            <DetailField label="Created At">{item.createdAt}</DetailField>
                                                            <DetailField label="Modified At">{item.modifiedAt}</DetailField>
                                                            {item.preview && (
                                                                <div className="col-12 mb-2">
                                                                    <div className="text-muted small">Preview</div>
                                                                    <img
                                                                        src={item.preview}
                                                                        alt={`${item.name || 'Template'} preview`}
                                                                        className="img-fluid rounded border mt-1"
                                                                        style={{ maxHeight: '160px' }}
                                                                    />
                                                                </div>
                                                            )}
                                                            {item.contentPath && (
                                                                <div className="col-12 mb-2">
                                                                    <div className="text-muted small">Content Path</div>
                                                                    <div className="small font-monospace text-break">
                                                                        {truncateText(item.contentPath, 500)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small text-muted">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>

            <Modal
                show={Boolean(previewTemplate)}
                onHide={handleClosePreview}
                size="xl"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h5 mb-0">
                        <i className="bi bi-eye me-2 text-info"></i>
                        {previewTemplate?.type === 'SMS' ? 'SMS' : 'Email'} Template Preview
                        {previewTemplate?.name ? (
                            <span className="text-muted fw-normal ms-2">· {previewTemplate.name}</span>
                        ) : null}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {previewLoading && (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status" variant="primary" />
                            <p className="text-muted small mt-3 mb-0">Loading template content…</p>
                        </div>
                    )}

                    {!previewLoading && previewError && (
                        <Alert variant="warning" className="m-3 mb-0">
                            {previewError}
                        </Alert>
                    )}

                    {!previewLoading && !previewError && previewContent && (
                        <>
                            <div className="px-3 py-2 border-bottom bg-light">
                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                    <TypeBadge type={previewContent.type || previewTemplate?.type} />
                                    <ActiveBadge isActive={previewContent.isActive ?? previewTemplate?.isActive} />
                                    {previewContent.preview ? (
                                        <img
                                            src={previewContent.preview}
                                            alt="Template thumbnail"
                                            className="rounded border"
                                            style={{ maxHeight: 48, maxWidth: 80, objectFit: 'contain' }}
                                        />
                                    ) : null}
                                </div>
                                <p className="text-muted small mb-0 mt-2">
                                    Preview only. Editing will not save anything.
                                </p>
                            </div>

                            {(previewContent.type || previewTemplate?.type) === 'SMS' ? (
                                <div className="p-4">
                                    <div className="border rounded bg-light p-3" style={{ whiteSpace: 'pre-wrap' }}>
                                        {previewContent.contentPath || 'No SMS content available.'}
                                    </div>
                                </div>
                            ) : (
                                <iframe
                                    title={`Preview of ${previewTemplate?.name || 'email template'}`}
                                    srcDoc={previewContent.contentPath || ''}
                                    style={{
                                        width: '100%',
                                        minHeight: '70vh',
                                        border: 'none',
                                        display: 'block',
                                        background: '#f5f5f5',
                                    }}
                                />
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleClosePreview}>
                        Close
                    </Button>
                    {previewTemplate?.id && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                handleClosePreview();
                                handleEdit(previewTemplate.id);
                            }}
                        >
                            <i className="bi bi-pencil me-1"></i>
                            Edit Template
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CampaignTemplateTable;
